import { useEffect, useState } from 'react';
import { Field, Select } from './ui';
import { ODU, composeCollectionName, parseCollectionName } from '../data/odu';

/**
 * Picks a Major + Minor Odù and composes them into a `collectionName`.
 * Options display the tonal Yorùbá spelling; the value passed to `onChange`
 * is the plain ASCII collection name (e.g. "EJI OGBE").
 *
 * Selections are held in local state so a single dropdown reflects immediately,
 * even before the other is chosen (`onChange` only fires once both are set —
 * a collectionName needs both). The local state re-syncs whenever the `value`
 * prop changes from outside (e.g. a different item is loaded, or a Reset).
 */
export function OduCollectionPicker({ value, onChange }: {
  value?: string;
  onChange: (collectionName: string) => void;
}) {
  const [majorName, setMajorName] = useState('');
  const [minorName, setMinorName] = useState('');

  useEffect(() => {
    const { major, minor } = parseCollectionName(value);
    setMajorName(major?.name ?? '');
    setMinorName(minor?.name ?? '');
  }, [value]);

  function pick(nextMajor: string, nextMinor: string) {
    setMajorName(nextMajor);
    setMinorName(nextMinor);
    const maj = ODU.find(o => o.name === nextMajor);
    const min = ODU.find(o => o.name === nextMinor);
    if (maj && min) onChange(composeCollectionName(maj, min));
  }

  return (
    <div>
      <div className="nb-odu-picker">
        <Field label="Major Odù">
          <Select value={majorName} onChange={e => pick(e.target.value, minorName)}>
            <option value="" disabled>Select Major Odù…</option>
            {ODU.map(o => (
              <option key={o.name} value={o.name}>{o.code}</option>
            ))}
          </Select>
        </Field>
        <Field label="Minor Odù">
          <Select value={minorName} onChange={e => pick(majorName, e.target.value)}>
            <option value="" disabled>Select Minor Odù…</option>
            {ODU.map(o => (
              <option key={o.name} value={o.name}>{o.code}</option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="nb-field__hint nb-odu-picker__preview">
        Collection: {value ? <strong>{value}</strong> : <em>select both Odù</em>}
      </div>
    </div>
  );
}
